/**
 * LOCKER-324 — the automatic pickup-reminder schedule, as pure arithmetic.
 *
 * Shared between `fnCheckReservationReminders` (which decides whether to send)
 * and the SA console's manual-send confirmation (which tells the operator when
 * the next automatic one is due). One module rather than two, because the two
 * numbers disagreeing is exactly the bug the confirmation exists to prevent.
 *
 * **Takes epoch milliseconds, never timestamp strings.** SST v1 emits ISO-Z and
 * SST v2 emits naive UTC (`"YYYY-MM-DD HH:mm:ss"`, memory `sstv2-datetime`), and
 * the two sides of the app already own different parsers for that
 * (`parseSstv2TimestampToMs` on the server, `parseSst2Utc` in the wfe). Keeping
 * this file parser-free is what lets the wfe import it without pulling server
 * code into the browser bundle.
 */

/** Hard cap on automatic + manual reminders per reservation — the ONE value.
 * The scan, `sendOrderPickupReminderHandler` (which enforces it on every send,
 * manual or automatic) and the console's reminder button all read it here. */
export const REMINDER_MAX_COUNT = 2;

/**
 * Bounds on `configReminderPeriodDays`. The max is what makes
 * `REMINDER_MAX_COUNT` reachable inside `REMINDER_MAX_PARCEL_AGE_DAYS`: at 7
 * days the 2nd reminder is due ~day 14, inside the 15-day ceiling.
 *
 * Enforced at READ time by `clampReminderPeriodDays`, not only at write time:
 * each tenant's Configuration row stores its own `min`/`max`, copied when the
 * tenant was created, so rows predating this cap still carry `max: 365`.
 */
export const REMINDER_MIN_PERIOD_DAYS = 1;
export const REMINDER_MAX_PERIOD_DAYS = 7;

/** Fallback when a tenant has no `configReminderPeriodDays` row. Matches the
 * config's own default (`configurationInternalData.configReminderPeriod`). */
export const DEFAULT_REMINDER_PERIOD_DAYS = 4;

/**
 * The `Configuration.refName` carrying the tenant's reminder cadence.
 *
 * Here rather than in each consumer because three of them need it — the scan
 * that reads it, the wfe hook that mirrors it, and the Configuration page that
 * decides whether to show it — and a fourth inline copy of a free-text refName
 * is exactly how a rename becomes a silent no-op.
 */
export const REMINDER_PERIOD_CONFIG_REF_NAME = "configReminderPeriodDays";

/**
 * The tenant's stored period, as the scan and the console apply it: the
 * default when absent or not a positive number, otherwise clamped to
 * [`REMINDER_MIN_PERIOD_DAYS`, `REMINDER_MAX_PERIOD_DAYS`].
 */
export const clampReminderPeriodDays = (days: unknown): number => {
  const n = typeof days === "number" ? days : Number(days);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_REMINDER_PERIOD_DAYS;
  return Math.min(
    REMINDER_MAX_PERIOD_DAYS,
    Math.max(REMINDER_MIN_PERIOD_DAYS, n),
  );
};

/**
 * The SST statuses a parcel sitting in a door awaiting pickup can carry.
 *
 * Both are required, and the pair is the whole v1/v2 story of this ticket. A
 * deposited reservation normally reads `DELIVERED` — on v2 the partner sends it
 * (`mapSstv2StatusToV1` passes it through), on v1 `fnSyncSstData` syncs it and
 * `extendReservationExpirationHandler` writes it. `CONFIRMED` is kept because a
 * v1 row can still be sitting on the pre-sync value with `delivery_time`
 * already set. The scan before LOCKER-324 filtered on `CONFIRMED` **alone**,
 * which is why it matched nothing on either version.
 */
export const AWAITING_PICKUP_SST_STATUSES = ["CONFIRMED", "DELIVERED"] as const;

export const reminderDaysToMs = (days: number) => days * 24 * 60 * 60 * 1000;

/**
 * How early a reminder still counts as due. `reminderLastSent` is written by
 * the send handler a few seconds AFTER the tick that triggered it, so exactly
 * one period later the same tick reads `now` a few seconds short of `dueAt`.
 * Without this margin every reminder after the first slipped one whole tick
 * (period + 1 h — seen on QA 2026-10-02, short by 2–3 s). Well under the
 * hourly tick, so it can never send one tick early.
 */
export const REMINDER_DUE_TOLERANCE_MS = 10 * 60 * 1000;

// ---------------------------------------------------------------------------
// Backlog guards
//
// This feature has been dark since it was written: no schedule, a swallowed
// trigger, and a filter that matched nothing. So the FIRST tick after a tenant
// is activated is not a normal tick — without the guards below it would look at
// every parcel ever deposited and still sitting unclaimed, find them all
// "overdue" (their period elapsed months ago), and fan out one real SMS or email
// per row in a single minute. To a real customer, about a parcel they may have
// long since dealt with.
//
// These are deliberately CONSTANTS, not tenant configuration. A ceiling a tenant
// can raise is not a ceiling; the tenant-tunable knob is
// `configReminderPeriodDays`, which sets cadence, not blast radius.
// ---------------------------------------------------------------------------

/**
 * **Never remind about a parcel deposited longer ago than this.** A hard ceiling
 * in days, independent of expiration and of the tenant's period.
 *
 * Why it is needed even with the expiry guard: `expiration_time` is not a fixed
 * horizon. `extendReservationExpirationHandler` pushes it out on request, and a
 * parcel whose expiry is extended repeatedly can sit in a door for months while
 * still reading "unexpired". A reminder about a two-month-old parcel is noise at
 * best and alarming at worst, and no cadence setting bounds it. This does.
 *
 * Measured from the DEPOSIT (`sstReservation.delivery_time`, falling back to
 * `createdAt`), never from the last reminder. 15, not 14: at the maximum period
 * (`REMINDER_MAX_PERIOD_DAYS` = 7) the 2nd reminder falls due ~day 14 plus up to
 * one hourly tick, and the extra day keeps it inside the ceiling.
 */
export const REMINDER_MAX_PARCEL_AGE_DAYS = 15;

/**
 * **Ceiling on reminders created per tick, across all tenants.** The tick runs
 * hourly, so this is the true rate limit on customer-visible messages.
 *
 * A backstop, not the primary control — `beforeActivation` and `tooOld` are what
 * make the backlog empty. This one bounds the damage from a selection bug nobody
 * has thought of yet, including in code written after this comment. Excess is
 * DEFERRED to the next tick, never dropped: the reservations stay eligible, so a
 * genuine backlog drains at a visible, interruptible rate instead of arriving at
 * once.
 */
export const REMINDER_MAX_PER_TICK = 50;

/** Same, per tenant, so one large tenant cannot consume the global budget and
 * starve every other tenant's reminders behind it. */
export const REMINDER_MAX_PER_TENANT_PER_TICK = 25;

/**
 * Why a reservation was not reminded. Logged, emitted as a metric, and asserted
 * on in tests, so a silent "no reminders today" can be told apart from a filter
 * that is wrong.
 *
 * `notDeposited` / `pickedUp` / `notAwaitingPickup` are three reasons rather than
 * one (LOCKER-324 review F1.5) because they are the answers to three different
 * questions. Collapsed into one counter, the log line and the metric that watch
 * this scan cannot say WHY nobody was reminded — which is the only question worth
 * asking of a function whose entire history is having silently done nothing.
 */
export type ReminderSkipReason =
  | "tenantNotActivated"
  | "maxRemindersSent"
  | "notDeposited"
  | "pickedUp"
  | "notAwaitingPickup"
  | "expired"
  /** No parseable `expiration_time`. Fails CLOSED — see `evaluateReminderDue`. */
  | "expirationUnknown"
  /** Deposited before this tenant activated the feature. Activation is not
   * retroactive; this is the guard that makes flipping the switch a no-op for
   * the whole historical backlog. */
  | "beforeActivation"
  /** Older than `REMINDER_MAX_PARCEL_AGE_DAYS`. */
  | "tooOld"
  | "periodNotElapsed"
  /** Genuinely due, but over this tick's cap — eligible again next tick. */
  | "deferredByCap";

export type ReminderScheduleInput = {
  /** `reservation.reminderLastSent` — epoch ms, absent until the first send. */
  lastSentMs?: number;
  /** `reservation.createdAt` as epoch ms. The clock starts here for reminder 1. */
  createdAtMs?: number;
  /** The tenant's `configReminderPeriodDays`, in ms. */
  periodMs: number;
  /** `reservation.nbReminderSent`. */
  nbReminderSent?: number;
};

/**
 * When the next automatic reminder becomes due, or `undefined` when there will
 * never be another one (the cap is spent, or we have no clock to count from).
 *
 * `undefined` is deliberately not "now": a caller that cannot establish a start
 * instant must not conclude the reminder is overdue.
 *
 * **The clock starts at `createdAt`, not at `delivery_time`, and that was a
 * choice** (LOCKER-324 review, DoD note 3). `delivery_time` — the instant the
 * parcel actually entered the door — is the semantically purer anchor for
 * "remind after x days", and the scan already reads it. `createdAt` is used
 * because in the normal flow the two are minutes apart (an unconfirmed draft
 * expires in `configDraftReservationExpirationMinutes`), and for the one case
 * where they are not — a migrated HISTORY reservation, whose `createdAt`
 * `flipReservationToV2` deliberately never moves — the expiry guard in
 * `evaluateReminderDue` already excludes the row long before the anchor matters.
 * Recorded rather than changed, so the next reader does not have to re-derive it;
 * if a case turns up where a long-deposited, unexpired parcel is reminded on the
 * first tick, `delivery_time` is the fix.
 */
export const nextReminderDueAtMs = ({
  lastSentMs,
  createdAtMs,
  periodMs,
  nbReminderSent = 0,
}: ReminderScheduleInput): number | undefined => {
  if (nbReminderSent >= REMINDER_MAX_COUNT) return undefined;
  if (!periodMs || periodMs <= 0) return undefined;
  const from = lastSentMs ?? createdAtMs;
  if (!from) return undefined;
  return from + periodMs;
};

export type ReminderDueEvaluation = {
  isDue: boolean;
  skipReason?: ReminderSkipReason;
  /** Present whenever one is computable, due or not — the wfe shows it. */
  dueAtMs?: number;
};

export type ReminderDueInput = ReminderScheduleInput & {
  nowMs: number;
  /** `sstReservation.status`. */
  sstStatus?: string;
  /** `sstReservation.delivery_time` — non-empty once the parcel is deposited. */
  deliveryTime?: string;
  /** `sstReservation.reception_time` — non-empty once the client picked up. */
  receptionTime?: string;
  /** `sstReservation.expiration_time` as epoch ms, when parseable. */
  expirationMs?: number;
  /**
   * `sstReservation.delivery_time` as epoch ms — when the parcel actually
   * entered the door. The anchor for the two backlog guards, and deliberately
   * NOT the same input as the cadence clock (`createdAtMs`): age is a property
   * of the parcel, cadence a property of our own last message.
   *
   * Falls back to `createdAtMs` when unparseable; when NEITHER resolves the age
   * guards fail closed, because an un-ageable row is exactly the kind of odd,
   * old record a backlog is made of.
   */
  depositedAtMs?: number;
  /**
   * When this tenant activated `AutomaticPickupReminder`, epoch ms. Anything
   * deposited before it is skipped as `beforeActivation`.
   *
   * Required in practice: the caller passes `undefined` only when it could not
   * resolve an activation instant at all, and that case fails closed (no
   * reminders for that tenant) rather than opening the floodgate.
   */
  featureActivatedAtMs?: number;
};

/**
 * The single decision: should this reservation get an automatic reminder now?
 *
 * Every guard lives here rather than in the DDB `FilterExpression`, because a
 * filter expression cannot be unit-tested and half of these need arithmetic the
 * expression language does not have. The scan's filter stays a cheap
 * pre-narrowing; this is the authority.
 */
export const evaluateReminderDue = ({
  nowMs,
  sstStatus,
  deliveryTime,
  receptionTime,
  expirationMs,
  depositedAtMs,
  featureActivatedAtMs,
  ...schedule
}: ReminderDueInput): ReminderDueEvaluation => {
  const dueAtMs = nextReminderDueAtMs(schedule);

  if ((schedule.nbReminderSent ?? 0) >= REMINDER_MAX_COUNT) {
    return { isDue: false, skipReason: "maxRemindersSent" };
  }

  // Ordered narrowest-cause-first, and the order is load-bearing: a picked-up
  // reservation normally carries a terminal STATUS too, so testing the status
  // first would report every collected parcel as `notAwaitingPickup` and the
  // `pickedUp` counter would never move.
  if (!deliveryTime) {
    return { isDue: false, skipReason: "notDeposited", dueAtMs };
  }
  if (receptionTime) {
    return { isDue: false, skipReason: "pickedUp", dueAtMs };
  }
  if (
    !AWAITING_PICKUP_SST_STATUSES.includes(
      sstStatus as (typeof AWAITING_PICKUP_SST_STATUSES)[number],
    )
  ) {
    return { isDue: false, skipReason: "notAwaitingPickup", dueAtMs };
  }

  // Time-based expiry, not just the status. SST tells us a reservation EXPIRED
  // on its own schedule (an event, or the once-a-day v1 sync), so between the
  // instant and the notification a row still reads DELIVERED — and reminding a
  // customer to collect a parcel the locker has already released is worse than
  // not reminding at all.
  //
  // ⚠ FAILS CLOSED on an unparseable or absent expiration. An earlier version of
  // this function sent in that case, on the reasoning that we should not *assume*
  // expiry. That reasoning is wrong for this feature: a row we cannot date is
  // overwhelmingly likely to be an old or irregular one — a migrated row, a
  // partner-materialised row, something written before a mapper fix — which is
  // precisely the population a first-activation backlog consists of. "Unknown"
  // must mean "do not message a customer", not "message them anyway".
  if (expirationMs === undefined) {
    return { isDue: false, skipReason: "expirationUnknown", dueAtMs };
  }
  if (expirationMs <= nowMs) {
    return { isDue: false, skipReason: "expired", dueAtMs };
  }

  // --- The two backlog guards. Both anchor on the DEPOSIT, not on our cadence.
  const parcelAgeAnchorMs = depositedAtMs ?? schedule.createdAtMs;
  if (parcelAgeAnchorMs === undefined) {
    // Cannot age the row at all -> cannot prove it is recent -> do not message.
    return { isDue: false, skipReason: "tooOld", dueAtMs };
  }

  // Activation is not retroactive. Turning the feature on for a tenant must not
  // reach back over parcels that were sitting there while it was off.
  if (
    featureActivatedAtMs === undefined ||
    parcelAgeAnchorMs < featureActivatedAtMs
  ) {
    return { isDue: false, skipReason: "beforeActivation", dueAtMs };
  }

  if (
    nowMs - parcelAgeAnchorMs >
    reminderDaysToMs(REMINDER_MAX_PARCEL_AGE_DAYS)
  ) {
    return { isDue: false, skipReason: "tooOld", dueAtMs };
  }

  if (dueAtMs === undefined || nowMs < dueAtMs - REMINDER_DUE_TOLERANCE_MS) {
    return { isDue: false, skipReason: "periodNotElapsed", dueAtMs };
  }

  return { isDue: true, dueAtMs };
};
